import type { ReactNode } from "react";
import { withTheme, type IChangeEvent } from "@rjsf/core";
import { Theme as MuiTheme } from "@rjsf/mui";
import { customizeValidator } from "@rjsf/validator-ajv8";
import type { RJSFSchema, UiSchema } from "@rjsf/utils";
import { customWidgets } from "../../components/widgets/widgetRegistry";
import { transformErrors } from "@/shared/utils/transformErrors";
import { LayoutGridField } from "../../components/fields/LayoutGridField";
import {
  DescriptionFieldTemplate,
  ArrayFieldItemTemplate,
  ObjectFieldTemplate,
  ErrorListTemplate,
  CustomArrayFieldTemplate,
} from "../../components/jsrfTemplates";

const Form = withTheme(MuiTheme);

// Module singletons: RJSF re-validates when the validator prop identity
// changes, and ajv caches compiled schemas per instance — keep one of each.
// The int/double formats are declared by report schemas but carry no JS
// validation semantics.
/* eslint-disable @typescript-eslint/no-unused-vars */
const validator = customizeValidator({
  customFormats: {
    int: (_data: string) => true,
    double: (_data: string) => true,
  },
});
/* eslint-enable @typescript-eslint/no-unused-vars */

const BASE_TEMPLATES = {
  DescriptionFieldTemplate,
  ArrayFieldItemTemplate,
  ObjectFieldTemplate,
  ErrorListTemplate,
  ArrayFieldTemplate: CustomArrayFieldTemplate,
};

export interface SchemaFormProps {
  schema: RJSFSchema;
  formData: unknown;
  uiSchema?: UiSchema;
  readonly?: boolean;
  disabled?: boolean;
  liveValidate?: boolean;
  autoComplete?: string;
  onChange?: (e: IChangeEvent) => void;
  /** Report-domain context consumed by the custom widgets/templates
   *  (easter-egg refs, websocket, chat bridge, AI controls, …). */
  formContext?: Record<string, unknown>;
  /** Shallow-merged over the shared defaults — pass e.g.
   *  `{ ButtonTemplates: { AddButton, RemoveButton } }` for editable forms. */
  templates?: Record<string, unknown>;
  /** Suppress RJSF's built-in submit button (renders empty children).
   *  Editable screens instead suppress it via ui:submitButtonOptions. */
  hideSubmit?: boolean;
  children?: ReactNode;
}

/**
 * The single RJSF wrapper for report schema forms: themed @rjsf/mui Form,
 * shared ajv validator (int/double formats), the KARDA template set,
 * widget registry, LayoutGridField, and transformErrors. Generic schema
 * editors with stock validation (JsonEditor, HitlInterruptPanel) must NOT
 * use this wrapper — it would silently disable int/double format checking
 * and inject report-domain widgets.
 */
export function SchemaForm({
  schema,
  formData,
  uiSchema,
  readonly,
  disabled,
  liveValidate = false,
  autoComplete,
  onChange,
  formContext,
  templates,
  hideSubmit = false,
  children,
}: SchemaFormProps) {
  return (
    <Form
      schema={schema}
      formData={formData}
      validator={validator}
      uiSchema={uiSchema}
      transformErrors={transformErrors}
      templates={{ ...BASE_TEMPLATES, ...templates }}
      widgets={customWidgets}
      fields={{ LayoutGridField }}
      readonly={readonly}
      disabled={disabled}
      liveValidate={liveValidate}
      autoComplete={autoComplete}
      onChange={onChange}
      formContext={formContext}
    >
      {hideSubmit ? <></> : children}
    </Form>
  );
}
